import React, { useState, useEffect, useRef } from 'react';
import { Link } from "react-router-dom";
import { useLanguage } from '../context/LanguageContext';
import '../styles/hot-news.css'; 
import sliderService from '../services/slider.service';
import commissionerService from '../services/commissioner.service';
import servicesService from '../services/services.service';
import hotNewsService from '../services/hotNews.service';
import sectionsService from '../services/sections.service';
import API_CONFIG from '../config/api.config';
import sanitizeHTML from '../utils/sanitizer';
function Home() {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([]);
  const [error, setError] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const hasFetched = useRef(false);
  const carouselRef = useRef(null);
  // Single page-level loading state 
  const [pageLoading, setPageLoading] = useState(true);
  // Commissioner and About section state 
  const [commissioner, setCommissioner] = useState({
    name: '',
    title: '',
    photo: '',
  });
  const [aboutContent, setAboutContent] = useState({
    title: '',
    content: '',
  });
  // Service cards state 
  const [services, setServices] = useState([]);
  // Hot News state 
  const [hotNews, setHotNews] = useState(null);
  // Who Are We section state 
  const [whoAreWeSection, setWhoAreWeSection] = useState(null);
  useEffect(() => {
    // Prevent double API calls in React Strict Mode 
    if (hasFetched.current) return;
    hasFetched.current = true;
    const fetchAllData = async () => {
      try {
        setPageLoading(true);
        const baseServerUrl = API_CONFIG.baseURL.replace('/cms', '');
        // Fetch all data in parallel with individual error handling 
        // Using Promise.allSettled ensures all APIs complete even if some fail 
        const results = await Promise.allSettled([
          sliderService.getPublishedSliders().catch(err => ({ success: false, error: err.message, data: null })),
          commissionerService.getCommissionerInfo().catch(err => ({ success: false, error: err.message, data: null })),
          commissionerService.getAboutContent().catch(err => ({ success: false, error: err.message, data: null })),
          servicesService.getServices().catch(err => ({ success: false, error: err.message, data: null })),
          hotNewsService.getHotNews().catch(err => ({ success: false, error: err.message, data: null })),
          sectionsService.getWhoAreWeSection().catch(err => ({ success: false, error: err.message, data: null }))
        ]);
        // Extract results from Promise.allSettled 
        const [
          slidersResult,
          commissionerResult,
          aboutResult,
          servicesResult,
          hotNewsResult,
          whoAreWeResult
        ] = results.map(result => result.status === 'fulfilled' ? result.value : { success: false, data: null });
        // Track API success/failure for professional logging 
        const apiStatus = {
          sliders: slidersResult.success,
          commissioner: commissionerResult.success,
          about: aboutResult.success,
          services: servicesResult.success,
          hotNews: hotNewsResult.success,
          whoAreWe: whoAreWeResult.success
        };
        const successCount = Object.values(apiStatus).filter(Boolean).length;
        // Transform all data before setting state 
        let finalSlides = [];
        let finalCommissioner = null;
        let finalAbout = {};
        let finalServices = [];
        let finalHotNews = null;
        let finalWhoAreWe = null;
        let slideError = null;
        // Process sliders 
        if (slidersResult.success && slidersResult.data.length > 0) {
          const transformedSlides = sliderService.transformSliderData(slidersResult.data);
          finalSlides = transformedSlides.map(slide => {
            let imageUrl = slide.image;
            if (imageUrl && !imageUrl.startsWith('http')) {
              imageUrl = imageUrl.startsWith('/')
                ? `${baseServerUrl}${imageUrl}`
                : `${baseServerUrl}/${imageUrl}`;
            }
            return { ...slide, image: imageUrl };
          });
        } else {
          // finalSlides = [{ 
          // id: 'default-1', 
          // image: '/assets/images/slide3.png', 
          // title: 'Welcome to Tanzania Immigration Services', 
          // subtitle: 'Your Gateway to Seamless Immigration Solutions', 
          // description: 'Fast, secure, and reliable immigration services for citizens and visitors', 
          // link: '', 
          // sortOrder: 0 
          // }]; 
        }
        // Process commissioner 
        if (commissionerResult.success && commissionerResult.data) {
          finalCommissioner = commissionerService.transformCommissionerData(commissionerResult.data);
          if (finalCommissioner.photo && !finalCommissioner.photo.startsWith('http') && !finalCommissioner.photo.startsWith('/assets')) {
            finalCommissioner.photo = finalCommissioner.photo.startsWith('/')
              ? `${baseServerUrl}${finalCommissioner.photo}`
              : `${baseServerUrl}/${finalCommissioner.photo}`;
          }
        }
        // Process about content 
        if (aboutResult.success && aboutResult.data) {
          finalAbout = commissionerService.transformAboutData(aboutResult.data);
        }
        // Process services 
        if (servicesResult.success && servicesResult.data && Array.isArray(servicesResult.data) && servicesResult.data.length > 0) {
          const transformedServices = servicesService.transformServiceData(servicesResult.data);
          finalServices = transformedServices.map(service => {
            let iconUrl = service.icon;
            if (iconUrl && !iconUrl.startsWith('http') && !iconUrl.startsWith('/assets')) {
              iconUrl = iconUrl.startsWith('/')
                ? `${baseServerUrl}${iconUrl}`
                : `${baseServerUrl}/${iconUrl}`;
            }
            return { ...service, icon: iconUrl, imageUrl: iconUrl };
          });
        }
        // Process hot news 
        if (hotNewsResult.success && hotNewsResult.data) {
          finalHotNews = hotNewsService.transformHotNewsData(hotNewsResult.data);
          if (finalHotNews.imageUrl && !finalHotNews.imageUrl.startsWith('http') && !finalHotNews.imageUrl.startsWith('/assets')) {
            finalHotNews.imageUrl = finalHotNews.imageUrl.startsWith('/')
              ? `${baseServerUrl}${finalHotNews.imageUrl}`
              : `${baseServerUrl}/${finalHotNews.imageUrl}`;
          }
        }
        // Process who are we section 
        if (whoAreWeResult.success && whoAreWeResult.data) {
          finalWhoAreWe = sectionsService.transformSectionData(whoAreWeResult.data);
          if (finalWhoAreWe.imageUrl && !finalWhoAreWe.imageUrl.startsWith('http') && !finalWhoAreWe.imageUrl.startsWith('/assets')) {
            finalWhoAreWe.imageUrl = finalWhoAreWe.imageUrl.startsWith('/')
              ? `${baseServerUrl}${finalWhoAreWe.imageUrl}`
              : `${baseServerUrl}/${finalWhoAreWe.imageUrl}`;
          }
        }
        // Batch all state updates together - this ensures all data renders at the same time 
        setSlides(finalSlides);
        setError(slideError);
        setCommissioner(finalCommissioner);
        setAboutContent(finalAbout);
        setServices(finalServices);
        setHotNews(finalHotNews);
        setWhoAreWeSection(finalWhoAreWe);
      } catch (err) {
        setError(`Failed to load data: ${err.message}`);
      } finally {
        setPageLoading(false);
      }
    };
    fetchAllData();
  }, []);
  useEffect(() => {
    if (isPaused || slides.length === 0) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length, isPaused]);
  // Start carousel at the first card and align 5 full cards per view
  useEffect(() => {
    if (services.length === 0) return;
    const timer = setTimeout(() => {
      const container = carouselRef.current;
      if (container && container.querySelector('.service-card')) {
        if (container.scrollWidth > container.clientWidth) {
          container.style.justifyContent = 'flex-start';
        } else {
          container.style.justifyContent = 'center';
        }
        container.scrollTo({ left: 0, behavior: 'auto' });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [services.length]);
  // Track scroll position to update active dot 
  useEffect(() => {
    const container = carouselRef.current;
    if (!container) return;
    const handleScroll = () => {
      const cardWidth = container.querySelector('.service-card')?.offsetWidth || 0;
      const gap = 20;
      const step = cardWidth + gap;
      const maxIndex = Math.max(0, services.length - 5);
      let index = Math.round(container.scrollLeft / step);
      index = Math.max(0, Math.min(index, maxIndex));
      setCurrentCardIndex(index);
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [services.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-layout">
          {/* Fixed Commissioner Panel - Left Side */}
          <div className="commissioner-panel">
            <div className="slide-commissioner-card">
              {commissioner ? (
                <>
                  <img
                    src={commissioner?.photo}
                    alt="Commissioner General"
                    className="slide-commissioner-photo"
                  />
                  <div className="commissioner-text-box">
                    <h3 className="commissioner-name">{commissioner?.name}</h3>
                    <p className="commissioner-title">{t('home.commissionerGeneral')}</p>
                  </div>
                </>
              ) : null}
            </div>
          </div>
          {/* Sliding Content - Right Side */}
          <div
            className="slider-container"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {error && (
              <div className="slider-error">
                <p>{error}</p>
              </div>
            )}
            {slides.map((slide, index) => (
              <div key={index} className={`slide-wrapper ${index === currentSlide ? 'active' : ''}`}>
                <img
                  src={slide.image}
                  alt={`Immigration Services Slide ${index + 1}`}
                  className="hero-image"
                />
                <div className="slide-overlay"></div>
                <div className="slide-caption">
                  <div className="slide-text-content">
                    <h1 className="caption-title">{slide.title}</h1>
                    {/* <p className="caption-subtitle">{slide.subtitle}</p> */}
                    <p className="caption-description">{slide.description}</p>
                    <div className="caption-buttons">
                      <Link to="/immigration-services" className="cta-primary">
                        {t('home.getStarted')}
                      </Link>
                      <Link to="/about-us" className="cta-secondary">
                        {t('home.getServiceInfo')}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {/* Navigation Arrows */}
            <button className="slider-arrow slider-arrow-left" onClick={prevSlide}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button className="slider-arrow slider-arrow-right" onClick={nextSlide}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {/* Dots Navigation */}
            <div className="slider-dots">
              {slides.map((_, index) => (
                <button
                  key={index}
                  className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Service Cards Section */}
      <section className="service-cards">
        <div className="container">
          <h2 className="section-title">{t('home.servicesTitle')}</h2>
          {services.length > 0 && (
            <div
              className="cards-carousel-wrapper"
              onMouseEnter={() => setIsCarouselPaused(true)}
              onMouseLeave={() => setIsCarouselPaused(false)}
            >
              <button
                className="carousel-arrow carousel-arrow-left"
                onClick={() => {
                  const container = document.querySelector('.cards-carousel');
                  if (container && container.querySelector('.service-card')) {
                    const cardWidth = container.querySelector('.service-card').offsetWidth;
                    const gap = 20;
                    const step = cardWidth + gap;
                    const newScrollLeft = Math.max(0, container.scrollLeft - step);
                    container.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
                  }
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <div className="cards-carousel" ref={carouselRef}>
                {services.map((service, index) => (
                  service.link && service.link !== '#' ? (
                    <a
                      key={service.id}
                      href={service.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`service-card ${service.cssClass}`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <img
                        src={service.imageUrl}
                        alt={service.title}
                      />
                      <h3>{service.title}</h3>
                      <p className="card-description">
                        {service.description?.replace(/<[^>]*>/g, '') || service.subtitle}
                      </p>
                      <button className={`apply-button ${servicesService.getButtonClass(service.name)}`}>
                        {t('services.applyNow')} ›
                      </button>
                    </a>
                  ) : (
                    <div key={service.id} className={`service-card ${service.cssClass}`}>
                      <img
                        src={service.imageUrl}
                        alt={service.title}
                      />
                      <h3>{service.title}</h3>
                      <p className="card-description">
                        {service.description?.replace(/<[^>]*>/g, '') || service.subtitle}
                      </p>
                      <button className={`apply-button ${servicesService.getButtonClass(service.name)}`}>
                        {t('services.applyNow')} ›
                      </button>
                    </div>
                  )
                ))}
              </div>
              <button
                className="carousel-arrow carousel-arrow-right"
                onClick={() => {
                  const container = document.querySelector('.cards-carousel');
                  if (container && container.querySelector('.service-card')) {
                    const cardWidth = container.querySelector('.service-card').offsetWidth;
                    const gap = 20;
                    const step = cardWidth + gap;
                    const maxScroll = container.scrollWidth - container.clientWidth;
                    const newScrollLeft = Math.min(maxScroll, container.scrollLeft + step);
                    container.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
                  }
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          )}

          {/* Navigation Dots */}
          {services.length > 0 && (
            <div className="carousel-dots">
              {services.map((_, index) => (
                <button
                  key={index}
                  className={`carousel-dot ${index === currentCardIndex ? 'active' : ''}`}
                  onClick={() => {
                    const container = carouselRef.current;
                    if (container && container.querySelector('.service-card')) {
                      const cardWidth = container.querySelector('.service-card').offsetWidth;
                      const gap = 20;
                      const step = cardWidth + gap;
                      const maxScroll = container.scrollWidth - container.clientWidth;
                      const targetPosition = Math.min(index * step, maxScroll);
                      container.scrollTo({ left: targetPosition, behavior: 'smooth' });
                    }
                  }}
                  aria-label={`Go to card ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Who Are We Section */}
      {/* <section className="who-are-we">
        <div className="container">
          <div className="who-are-we-content">
            <div className="who-are-we-text">
              {whoAreWeSection ? (
                <>
                  <div className="section-header with-image">
                    <h1>{whoAreWeSection?.title}</h1>
                    <img
                      src={whoAreWeSection?.imageUrl || whoAreWeSection?.link}
                      alt="Mjue Jirani Yako" className="mjue-logo"
                    />
                  </div>
                  <p>
                    <span dangerouslySetInnerHTML={{
                      __html: sanitizeHTML(
                        whoAreWeSection?.content
                          ? (whoAreWeSection.content.length > 286
                              ? `${whoAreWeSection.content.substring(0, 286)}...`
                              : whoAreWeSection.content)
                          : ''
                      )
                    }} />
                    {' '}
                    <Link to="/about-us" className="more-link">{t('home.readMore')} ▶</Link>
                  </p>
                  <Link to="/public-notice" className="news-notice-link">{t('publicNotice.title')} ▶</Link>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </section> */}
      {/* Hot News Section */}

      {hotNews && (
        <section className="hot-news-section">
          <div className="container">
            <div className="hot-news-container">
              {hotNews && (
                <div className="hot-news-content">
                  <div className="job-vacancy-icon">
                    <div className="bell-icon">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 2C11.4477 2 11 2.44772 11 3V3.17071C8.83481 3.58254 7.17071 5.24664 6.75888 7.41183L6 11H18L17.2411 7.41183C16.8293 5.24664 15.1652 3.58254 13 3.17071V3C13 2.44772 12.5523 2 12 2Z"
                          fill="currentColor"
                        />
                        <path
                          d="M6 13H18V14C18 16.2091 16.2091 18 14 18H10C7.79086 18 6 16.2091 6 14V13Z"
                          fill="currentColor"
                        />
                        <path
                          d="M10 19C10 19.5523 10.4477 20 11 20H13C13.5523 20 14 19.5523 14 19C14 18.4477 13.5523 18 13 18H11C10.4477 18 10 18.4477 10 19Z"
                          fill="currentColor"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="hot-news-text">
                    <h3 className="job-vacancy-title">{hotNews.title}</h3>
                    <p className="hot-news-description">
                      {hotNews.content
                        ? `${hotNews.content.replace(/<[^>]*>/g, '').substring(0, 230).trimEnd()}...`
                        : ''
                      }
                    </p>
                  </div>
                  <div className="job-vacancy-action">
                    <Link
                      to="/public-notice"
                      state={{ fromHotNews: true, hotNews: hotNews }}
                      className="apply-now-btn"
                    >
                      {t('home.readMore')} →
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
export default Home;

